import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import api from '../services/api';
import { FiPlus, FiClock, FiCheck, FiX, FiEye, FiBook, FiMessageSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const Requests = ({ onLogout }) => {
  const navigate = useNavigate();
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const isStudent = user?.role === 'student';

  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setRejectingId(null);
      setRejectReason('');
      let endpoint;

      if (isStudent) {
        endpoint = '/requests/my';
      } else if (activeTab === 'pending') {
        endpoint = '/requests/pending';
      } else {
        endpoint = '/requests/reviewed';
      }

      const response = await api.get(endpoint);
      setRequests(response || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setMessage('Error loading requests');
    } finally {
      setLoading(false);
    }
  }, [activeTab, isStudent]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve and publish this note to the library?')) {
      return;
    }

    try {
      setActionLoading(id);
      await api.put(`/requests/${id}/approve`);
      setMessage('Request approved and published successfully!');
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      setMessage(`Error: ${error.response?.data?.message || 'Failed to approve'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    if (!rejectReason || rejectReason.trim() === '') {
      alert('Please state a rejection reason.');
      return;
    }

    try {
      setActionLoading(id);
      await api.put(`/requests/${id}/reject`, {
        teacherMessage: rejectReason.trim()
      });
      setMessage('Request rejected successfully');
      setRejectingId(null);
      setRejectReason('');
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      setMessage(`Error: ${error.response?.data?.message || 'Failed to reject'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return <span className="badge badge-student"><FiCheck /> Approved</span>;
    }
    if (status === 'rejected') {
      return <span className="badge badge-admin"><FiX /> Rejected</span>;
    }
    return <span className="badge badge-teacher"><FiClock /> Pending Review</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Student View
  if (isStudent) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />

        <div className="main-content">
          <Header
            title="My Note Requests"
            subtitle="Track the approval status of study materials submitted for review."
          />

          <div className="d-flex justify-between align-center mb-4">
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Submitted Requests ({requests.length})</h2>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/upload')}
            >
              <FiPlus /> Submit New Request
            </button>
          </div>

          {message && (
            <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'} mb-4`}>
              {message}
            </div>
          )}

          {loading ? (
            <LoadingSkeleton type="card" count={3} />
          ) : requests.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {requests.map((request) => (
                <div key={request._id} className="card" style={{ marginBottom: 0 }}>
                  <div className="d-flex justify-between align-center mb-3" style={{ flexWrap: 'wrap', gap: '12px' }}>
                    <div className="d-flex align-center gap-3">
                      <div className="stat-icon-wrapper note" style={{ width: '42px', height: '42px', fontSize: '18px', flexShrink: 0 }}>
                        <FiBook />
                      </div>
                      <div>
                        <h3 style={{ fontSize: '17px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>
                          {request.title}
                        </h3>
                        <span className="text-sm text-secondary">
                          Submitted on {formatDate(request.createdAt)}
                        </span>
                      </div>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>

                  {request.description && (
                    <p className="text-secondary text-sm mb-3" style={{ background: 'var(--bg-surface-hover)', padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                      {request.description}
                    </p>
                  )}

                  <div className="d-flex align-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                    <span className="department-badge"><FiBook style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Subject: {request.subject}</span>
                    <span className="department-badge">Sem {request.semester}</span>
                    <span className="department-badge">Dept: {request.department}</span>
                  </div>

                  {request.status !== 'pending' && request.teacherMessage && (
                    <div className="alert alert-warning mb-3" style={{ fontSize: '13px' }}>
                      <FiMessageSquare /> Feedback: {request.teacherMessage}
                    </div>
                  )}

                  <div className="pt-3 d-flex justify-between align-center" style={{ borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap', gap: '12px' }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => window.open(`${process.env.REACT_APP_API_URL}${request.fileUrl}`, '_blank')}
                    >
                      <FiEye /> View Document
                    </button>
                    {request.status === 'approved' && (
                      <span className="text-sm" style={{ color: 'var(--success)', fontWeight: '600' }}>
                        ✓ Published to library
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<FiClock />}
              title="No note requests submitted yet"
              description="When you submit notes for review, your request status will appear here."
              actionButton={
                <button className="btn btn-primary" onClick={() => navigate('/upload')}>
                  <FiPlus /> Submit Note Request
                </button>
              }
            />
          )}
        </div>
      </div>
    );
  }

  // Teacher / Admin View
  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />

      <div className="main-content">
        <Header
          title="Review Note Requests"
          subtitle="Review, approve, or reject student note submissions."
        />

        {message && (
          <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'} mb-4`}>
            {message}
          </div>
        )}

        {/* Tab Buttons */}
        <div className="theme-switcher mb-4" style={{ padding: '4px', width: 'fit-content' }}>
          <button
            type="button"
            className={`theme-option-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <FiClock /> Pending Requests
          </button>
          <button
            type="button"
            className={`theme-option-btn ${activeTab === 'reviewed' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviewed')}
          >
            <FiCheck /> Reviewed Requests
          </button>
        </div>

        {loading ? (
          <LoadingSkeleton type="card" count={3} />
        ) : requests.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {requests.map((request) => (
                <div key={request._id} className="card" style={{ marginBottom: 0 }}>
                  <div className="d-flex justify-between align-center mb-3" style={{ flexWrap: 'wrap', gap: '12px' }}>
                    <div className="d-flex align-center gap-3">
                      <div className="stat-icon-wrapper note" style={{ width: '42px', height: '42px', fontSize: '18px', flexShrink: 0 }}>
                        <FiBook />
                      </div>
                      <div>
                        <h3 style={{ fontSize: '17px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>
                          {request.title}
                        </h3>
                        <span className="text-sm text-secondary">
                          Submitted by: <strong style={{ color: 'var(--text-primary)' }}>{request.requestedBy?.name}</strong> (<code>{request.requestedBy?.email}</code>)
                        </span>
                      </div>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>

                  {request.description && (
                    <p className="text-secondary text-sm mb-3" style={{ background: 'var(--bg-surface-hover)', padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                      {request.description}
                    </p>
                  )}

                  <div className="d-flex align-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                    <span className="department-badge"><FiBook style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Subject: {request.subject}</span>
                    <span className="department-badge">Sem {request.semester}</span>
                    <span className="department-badge">Dept: {request.department}</span>
                    <span className="text-sm text-muted ms-auto">Date: {formatDate(request.createdAt)}</span>
                  </div>

                  {request.status !== 'pending' && request.teacherMessage && (
                    <div className="alert alert-warning mb-3" style={{ fontSize: '13px' }}>
                      <FiMessageSquare /> Feedback: {request.teacherMessage}
                    </div>
                  )}

                  <div className="pt-3 d-flex justify-between align-center" style={{ borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap', gap: '12px' }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => window.open(`${process.env.REACT_APP_API_URL}${request.fileUrl}`, '_blank')}
                    >
                      <FiEye /> View Document
                    </button>

                    {request.status === 'pending' && (
                      <div className="d-flex gap-2 align-center" style={{ flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleApprove(request._id)}
                          disabled={actionLoading === request._id}
                        >
                          <FiCheck /> {actionLoading === request._id ? 'Approving...' : 'Approve & Publish'}
                        </button>

                        {rejectingId === request._id ? (
                          <div className="d-flex gap-2 align-center" style={{ flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              className="form-input"
                              style={{ height: '34px', fontSize: '13px', width: '220px' }}
                              placeholder="Reason for rejection..."
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                            />
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleReject(request._id)}
                              disabled={actionLoading === request._id}
                            >
                              Confirm Reject
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline"
                            style={{ color: 'var(--danger)', borderColor: 'var(--border-subtle)' }}
                            onClick={() => setRejectingId(request._id)}
                            disabled={actionLoading === request._id}
                          >
                            <FiX /> Reject
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<FiCheck />}
            title={`No ${activeTab} requests found`}
            description={activeTab === 'pending' ? 'You are all caught up! No pending student note requests.' : 'No reviewed request records found.'}
          />
        )}
      </div>
    </div>
  );
};

export default Requests;